import { DateContext } from '@/hooks/use-date-context';
import { ReactNode, useEffect, useState } from 'react';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Serialise a Date to a YYYY-MM-DD string using its LOCAL calendar parts.
 * Never use .toISOString() here — that converts to UTC and can shift the date
 * by up to ±14 hours depending on the browser timezone.
 */
const toDateString = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Parse a YYYY-MM-DD string back into a midday Date (avoids DST edge-cases).
 * Returns null if the string is falsy or malformed.
 */
const fromDateString = (s: string | null | undefined): Date | null => {
    if (!s) return null;
    const parts = s.split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return null;
    const [y, m, d] = parts;
    const date = new Date(y, m - 1, d, 12, 0, 0, 0);
    return isNaN(date.getTime()) ? null : date;
};

/** Today at midday in the browser's local timezone. */
const localToday = (): Date => {
    const now = new Date();
    return new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        12,
        0,
        0,
        0,
    );
};

const STORAGE_KEY = 'moteldeed_dates';

interface StoredDates {
    start: string | null;
    end: string | null;
}

/**
 * Read the initial dates synchronously from localStorage.
 * This runs once at module evaluation time inside a useState lazy initializer,
 * ensuring the very first render already has the correct persisted dates.
 * Doing this synchronously prevents a second setState call from the hydration
 * useEffect, which would trigger duplicate availability fetches downstream.
 */
const getInitialDates = (): { start: Date; end: Date } => {
    const today = localToday();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const { start, end } = JSON.parse(raw) as StoredDates;
            let parsedStart = fromDateString(start);
            let parsedEnd = fromDateString(end);

            if (parsedStart && parsedEnd) {
                // Shift past ranges forward preserving stay duration.
                if (parsedStart < today) {
                    const duration =
                        parsedEnd.getTime() - parsedStart.getTime();
                    parsedStart = new Date(today);
                    parsedEnd = new Date(today.getTime() + duration);
                }
                return { start: parsedStart, end: parsedEnd };
            }
        }
    } catch {
        // Corrupt data — fall through to defaults.
    }

    return { start: today, end: tomorrow };
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function DateProvider({ children }: { children: ReactNode }) {
    // Initialise synchronously from localStorage so the very first render
    // already carries the correct dates — no second setState / re-render.
    const [startDate, setStartDate] = useState<Date | null>(
        () => getInitialDates().start,
    );
    const [endDate, setEndDate] = useState<Date | null>(
        () => getInitialDates().end,
    );

    // ── Persist to localStorage whenever dates change ─────────────────────────
    useEffect(() => {
        const payload: StoredDates = {
            start: startDate ? toDateString(startDate) : null,
            end: endDate ? toDateString(endDate) : null,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));

        // Notify other parts of the app (e.g. mini date display in nav)
        window.dispatchEvent(
            new CustomEvent('datesChanged', { detail: { startDate, endDate } }),
        );
    }, [startDate, endDate]);

    // ── Setters with swap-guard ───────────────────────────────────────────────

    const handleSetStartDate = (date: Date | null) => {
        if (date && endDate && date > endDate) {
            // Swapped — flip them
            setStartDate(endDate);
            setEndDate(date);
        } else {
            setStartDate(date);
        }
    };

    const handleSetEndDate = (date: Date | null) => {
        if (date && startDate && date < startDate) {
            setEndDate(startDate);
            setStartDate(date);
        } else {
            setEndDate(date);
        }
    };

    const setDates = (start: Date | null, end: Date | null) => {
        setStartDate(start);
        setEndDate(end);
    };

    const clearDates = () => {
        setStartDate(null);
        setEndDate(null);
    };

    return (
        <DateContext.Provider
            value={{
                startDate,
                endDate,
                setStartDate: handleSetStartDate,
                setEndDate: handleSetEndDate,
                setDates,
                clearDates,
            }}
        >
            {children}
        </DateContext.Provider>
    );
}
